define(["exports", "./biblio", "./utils"], function (_exports, _biblio, _utils) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.run = run;
  _exports.linkInlineCitations = linkInlineCitations;
  _exports.name = void 0;
  // @ts-check

  /**
   * Module core/data-cite
   *
   * Allows citing other specifications using
   * anchor elements. Simply add "data-cite"
   * and key of specification.
   *
   * This module simply adds the found key
   * to either conf.normativeReferences
   * or to conf.informativeReferences depending on
   * if it starts with a ! or not.
   *
   * Usage:
   * https://github.com/w3c/respec/wiki/data--cite
   */
  const name = "core/data-cite";
  _exports.name = name;

  function requestLookup(conf) {
    const toCiteDetails = citeDetailsConverter(conf);
    return async elem => {
      const originalKey = elem.dataset.cite;
      const {
        key,
        frag,
        path
      } = toCiteDetails(elem);
      let href = "";
      let title = ""; // This is just referring to this document

      if (key.toLowerCase() === conf.shortName.toLowerCase()) {
        console.log(elem, "The reference \"".concat(key, "\" is resolved into the current document per `conf.shortName`."));
        href = document.location.href;
      } else {
        // Let's go look it up in spec ref...
        const entry = await (0, _biblio.resolveRef)(key);
        cleanElement(elem);

        if (!entry) {
          (0, _utils.showInlineWarning)(elem, "Couldn't find a match for \"".concat(originalKey, "\""));
          return;
        }

        href = entry.href;
        title = entry.title;
      }

      if (path) {
        // See: https://github.com/w3c/respec/issues/1856#issuecomment-429579475
        const relPath = path.startsWith("/") ? ".".concat(path) : path;
        href = new URL(relPath, href).href;
      }

      if (frag) {
        href = new URL(frag, href).href;
      }

      switch (elem.localName) {
        case "a":
          {
            if (elem.textContent === "" && elem.dataset.lt !== "the-empty-string") {
              elem.textContent = title;
            }

            elem.href = href;

            if (!path && !frag) {
              const cite = document.createElement("cite");
              elem.replaceWith(cite);
              cite.append(elem);
            }

            break;
          }

        case "dfn":
          {
            const anchor = document.createElement("a");
            anchor.href = href;

            if (!elem.textContent) {
              anchor.textContent = title;
              elem.append(anchor);
            } else {
              (0, _utils.wrapInner)(elem, anchor);
            }

            if (!path && !frag) {
              const cite = document.createElement("cite");
              cite.append(anchor);
              elem.append(cite);
            }

            if ("export" in elem.dataset) {
              (0, _utils.showInlineError)(elem, "Exporting an linked external definition is not allowed. Please remove the `data-export` attribute", "Please remove the `data-export` attribute.");
              delete elem.dataset.export;
            }

            elem.dataset.noExport = "";
            break;
          }
      }
    };
  }

  function cleanElement(elem) {
    ["data-cite", "data-cite-frag"].filter(attrName => elem.hasAttribute(attrName)).forEach(attrName => elem.removeAttribute(attrName));
  }
  /**
   * @param {string} component
   * @return {(key: string) => string}
   */


  function makeComponentFinder(component) {
    return key => {
      const position = key.search(component);
      return position !== -1 ? key.substring(position) : "";
    };
  }
  /**
   * @typedef {object} CiteDetails
   * @property {string} key
   * @property {boolean} isNormative
   * @property {string} frag
   * @property {string} path
   *
   * @return {(elem: HTMLElement) => CiteDetails};
   */


  function citeDetailsConverter(conf) {
    const findFrag = makeComponentFinder("#");
    const findPath = makeComponentFinder("/");
    return function toCiteDetails(elem) {
      const {
        dataset
      } = elem;
      const {
        cite: rawKey,
        citeFrag,
        citePath
      } = dataset; // The key is a fragment, resolve using the shortName as key

      if (rawKey.startsWith("#") && !citeFrag) {
        // Closes data-cite not starting with "#"

        /** @type {HTMLElement} */
        const closest = elem.parentElement.closest("[data-cite]:not([data-cite^=\"#\"])");
        const {
          key: parentKey,
          isNormative: closestIsNormative
        } = closest ? toCiteDetails(closest) : {
          key: conf.shortName || "",
          isNormative: false
        };
        dataset.cite = closestIsNormative ? parentKey : "?".concat(parentKey);
        dataset.citeFrag = rawKey.replace("#", ""); // the key is acting as fragment

        return toCiteDetails(elem);
      }

      const frag = citeFrag ? "#".concat(citeFrag) : findFrag(rawKey);
      const path = citePath || findPath(rawKey).split("#")[0]; // path is always before "#"

      const {
        type
      } = (0, _utils.refTypeFromContext)(rawKey, elem);
      const isNormative = type === "normative"; // key is before "/" and "#" but after "!" or "?" (e.g., ?key/path#frag)

      const hasPrecedingMark = /^[?|!]/.test(rawKey);
      const key = rawKey.split(/[/|#]/)[0].substring(Number(hasPrecedingMark));
      const details = {
        key,
        isNormative,
        frag,
        path
      };
      return details;
    };
  }

  async function run(conf) {
    const toCiteDetails = citeDetailsConverter(conf);
    /** @type {NodeListOf<HTMLElement>} */

    const cites = document.querySelectorAll("dfn[data-cite], a[data-cite]");
    Array.from(cites).filter(el => el.dataset.cite).map(toCiteDetails) // it's not the same spec
    .filter(({
      key
    }) => {
      return key.toLowerCase() !== (conf.shortName || "").toLowerCase();
    }).forEach(({
      isNormative,
      key
    }) => {
      if (!isNormative && !conf.normativeReferences.has(key)) {
        conf.informativeReferences.add(key);
        return;
      }

      conf.normativeReferences.add(key);
      conf.informativeReferences.delete(key);
    });
  }
  /**
   * @param {Document} doc
   * @param {*} conf
   */


  async function linkInlineCitations(doc, conf = respecConfig) {
    const toLookupRequest = requestLookup(conf);
    const elems = [...doc.querySelectorAll("dfn[data-cite]:not([data-cite='']), a[data-cite]:not([data-cite=''])")];
    const citeConverter = citeDetailsConverter(conf);
    const promisesForMissingEntries = elems.map(citeConverter).map(async entry => {
      const result = await (0, _biblio.resolveRef)(entry.key);
      return {
        entry,
        result
      };
    });
    const bibEntries = await Promise.all(promisesForMissingEntries);
    const missingBibEntries = bibEntries.filter(({
      result
    }) => result === null).map(({
      entry: {
        key
      }
    }) => key); // we now go to network to fetch missing entries

    const newEntries = await (0, _biblio.updateFromNetwork)(missingBibEntries);
    if (newEntries) Object.assign(_biblio.biblio, newEntries);
    const lookupRequests = [...new Set(elems)].map(toLookupRequest);
    return await Promise.all(lookupRequests);
  }
});
//# sourceMappingURL=data-cite.js.map